import { useContext, useSyncExternalStore } from 'react';
import { ReactReduxContext } from 'react-redux';
import type { RootState } from '@/app/store';
import { selectDisplayFormat } from '@/app/store/selectedEntitySlice';
import type { DisplayFormat } from '@/shared/utils/displayFormat';

const NO_STORE_SUBSCRIPTION = () => () => undefined;

/**
 * The selected entity's date, time and zone -- its own, else its organization's Site Configuration, resolved by the
 * server -- or null while none is known (2026-10-14).
 *
 * Read through the store when one is mounted, which the app always does, and null without one, so a control
 * rendered on its own (its tests do) still draws, in the platform defaults. Shared by every calendar and date filter
 * that draws a day in the entity's format: Organization Setup, the masters, Templates, Purchase Requests, Supplier.
 */
export function useEntityDisplayFormatOptional(): DisplayFormat | null {
  const store = useContext(ReactReduxContext)?.store;
  return useSyncExternalStore(store ? store.subscribe : NO_STORE_SUBSCRIPTION, () =>
    store ? selectDisplayFormat(store.getState() as RootState) : null,
  );
}

/** The selected entity's date pattern alone, or null -- see {@link useEntityDisplayFormatOptional}. */
export function useEntityDatePattern(): string | null {
  return useEntityDisplayFormatOptional()?.datePattern ?? null;
}
